import React, { useMemo } from 'react';
import { LockClosedIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';

const CATEGORY = {
    milestone: 'Milestones',
    learning: 'Learning',
    challenge: 'Practical challenges',
    professional: 'Professional journey',
    hours: 'Practical hours',
    completion: 'Completion'
};
const ORDER = ['milestone', 'learning', 'challenge', 'professional', 'hours', 'completion'];

function formatDate(value) {
    if (!value) return '';
    try {
        return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
        return '';
    }
}

/**
 * Every achievement: earned ones with their date, locked ones greyed out with
 * progress where it can be counted ("2 / 5"). Awarded automatically from the
 * intern's real activity on the platform.
 */
export default function AchievementsPanel({ achievements }) {
    const { groups, earnedCount, total } = useMemo(() => {
        const awarded = (achievements && achievements.awarded) || [];
        const available = (achievements && achievements.available) || [];
        const earnedByKey = Object.fromEntries(awarded.map((a) => [a.key, a]));
        const items = available.map((d) => ({ ...d, earned: earnedByKey[d.key] || null }));
        // Anything earned that is no longer listed (e.g. retired) still counts.
        for (const a of awarded) {
            if (!items.some((i) => i.key === a.key)) items.push({ ...a, earned: a });
        }
        const byCategory = {};
        for (const item of items) (byCategory[item.category || 'milestone'] = byCategory[item.category || 'milestone'] || []).push(item);
        const keys = [...ORDER.filter((k) => byCategory[k]), ...Object.keys(byCategory).filter((k) => !ORDER.includes(k))];
        return {
            groups: keys.map((k) => ({ key: k, label: CATEGORY[k] || k, items: byCategory[k] })),
            earnedCount: items.filter((i) => i.earned).length,
            total: items.length
        };
    }, [achievements]);

    return (
        <section className="card space-y-5 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <TrophyIcon className="h-5 w-5 text-ink-700" />
                    <h2 className="text-lg font-semibold text-ink-950">Achievements</h2>
                </div>
                {total > 0 && (
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-ink-600"><span className="font-semibold text-ink-950">{earnedCount}</span> of {total} earned</span>
                        <div className="h-2 w-32 overflow-hidden rounded-full bg-ink-100">
                            <div className="h-full rounded-full bg-brand-700" style={{ width: `${Math.round((earnedCount / total) * 100)}%` }} />
                        </div>
                    </div>
                )}
            </div>

            {total === 0 ? (
                <p className="text-sm text-ink-500">No achievements yet. They unlock automatically as you learn, complete challenges and share your journey.</p>
            ) : groups.map((group) => (
                <div key={group.key}>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-500">{group.label}</h3>
                    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        {group.items.map((item) => <AchievementCard key={item.key} item={item} />)}
                    </ul>
                </div>
            ))}
        </section>
    );
}

function AchievementCard({ item }) {
    const earned = Boolean(item.earned);
    const progress = item.progress;
    const pct = progress && progress.target ? Math.min(100, Math.round((progress.current / progress.target) * 100)) : 0;

    return (
        <li className={`flex gap-3 rounded-xl border p-3.5 ${earned ? 'border-brand-200 bg-brand-50/60' : 'border-border bg-white'}`}>
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${earned ? 'bg-brand-700 text-white' : 'bg-ink-100 text-ink-400'}`}>
                {earned ? <CheckBadgeIcon className="h-6 w-6" /> : <LockClosedIcon className="h-5 w-5" />}
            </span>
            <div className="min-w-0 flex-1">
                <p className={`text-sm font-semibold ${earned ? 'text-ink-950' : 'text-ink-700'}`}>{item.title}</p>
                <p className="mt-0.5 text-xs text-ink-500">{item.description}</p>
                {earned ? (
                    <p className="mt-1.5 text-[11px] font-medium text-brand-700">Earned {formatDate(item.earned.awarded_at)}</p>
                ) : progress && progress.target > 1 ? (
                    <div className="mt-2">
                        <div className="flex justify-between text-[11px] text-ink-500">
                            <span>Progress</span>
                            <span>{Math.min(progress.current, progress.target)} / {progress.target}</span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-ink-100">
                            <div className="h-full rounded-full bg-brand-600" style={{ width: `${pct}%` }} />
                        </div>
                    </div>
                ) : (
                    <p className="mt-1.5 text-[11px] text-ink-400">Locked</p>
                )}
            </div>
        </li>
    );
}
